// src/Components/HighLevelComponents/Tool/renderers/QuizRenderer.tsx
import React, { useState, useContext, useEffect } from "react";
import { ToolRendererProps, QuizResult } from "../types";
import {
  FaCopy,
  FaFileExport,
  FaFileWord,
  FaDownload,
  FaBook,
} from "react-icons/fa";
import { AiFillSave } from "react-icons/ai";
import { BiExport } from "react-icons/bi";
import toast from "react-hot-toast";
import { GoogleAnalyticsContext } from "../../../../Hooks/GoogleAnalyticsContext";
import { useNavigate } from "react-router-dom";
import { useFirebaseAuth } from "../../../../Hooks/FirebaseAuth";
import { YippityUserContext } from "../../../../Hooks/YippityUserContext";
import { useAuthenticatedFetch } from "../../../../Hooks/useFetch";
import config from "../../../../config";
import { HashLoader } from "react-spinners";

interface QuizQuestionProps {
  question: string;
  answer: string;
  index: number;
  isPracticeMode: boolean;
}

const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question,
  answer,
  index,
  isPracticeMode,
}) => {
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const [userAnswer, setUserAnswer] = useState("");
  const [showAnswer, setShowAnswer] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [feedback, setFeedback] = useState<
    "correct" | "partially" | "incorrect" | null
  >(null);

  const handleCheck = () => {
    if (!userAnswer.trim()) return;

    // Compare user answer with the correct answer
    const userAnswerLower = userAnswer.toLowerCase().trim();
    const correctAnswerLower = answer.toLowerCase().trim();

    // Simplified scoring logic - in a real app this could be more sophisticated
    if (userAnswerLower === correctAnswerLower) {
      setFeedback("correct");
      logEvent(
        "QuizInteraction",
        "AnswerCorrect",
        `quiz_question_${index + 1}`
      );
    } else if (
      correctAnswerLower.includes(userAnswerLower) ||
      userAnswerLower.includes(correctAnswerLower) ||
      userAnswerLower.length > 10
    ) {
      // If answer is substantial
      setFeedback("partially");
      logEvent(
        "QuizInteraction",
        "AnswerPartial",
        `quiz_question_${index + 1}`
      );
    } else {
      setFeedback("incorrect");
      logEvent(
        "QuizInteraction",
        "AnswerIncorrect",
        `quiz_question_${index + 1}`
      );
    }

    setShowAnswer(true);
    setReviewed(true);
  };

  const handleReset = () => {
    setUserAnswer("");
    setShowAnswer(false);
    setReviewed(false);
    setFeedback(null);
    logEvent("QuizInteraction", "Reset", `quiz_question_${index + 1}`);
  };

  const copyToClipboard = async () => {
    try {
      const text = `Question: ${question}\nAnswer: ${answer}`;
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
      logEvent(
        "Export",
        "CopyQuestionToClipboard",
        `quiz_question_${index + 1}`
      );
    } catch (err) {
      toast.error("Failed to copy to clipboard");
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-semibold text-gray-800 text-lg">
          Question {index + 1}
        </h3>
        <div className="flex items-center">
          <button
            onClick={copyToClipboard}
            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none mr-2"
            title="Copy to clipboard"
          >
            <FaCopy size={18} />
          </button>
          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded">
            Open Question
          </span>
        </div>
      </div>

      <p className="text-gray-700 mb-4">{question}</p>

      <div className="mb-4">
        {isPracticeMode && (
          <textarea
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="Type your answer here..."
            rows={3}
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            disabled={reviewed}
          />
        )}
      </div>

      {showAnswer && (
        <div
          className={`
          p-4 rounded-lg mb-4
          ${
            feedback === "correct"
              ? "bg-green-50 border border-green-200"
              : feedback === "partially"
              ? "bg-yellow-50 border border-yellow-200"
              : "bg-red-50 border border-red-200"
          }
        `}
        >
          <div className="flex items-center">
            <div
              className={`
              w-6 h-6 rounded-full flex items-center justify-center mr-2 text-white
              ${
                feedback === "correct"
                  ? "bg-green-500"
                  : feedback === "partially"
                  ? "bg-yellow-500"
                  : "bg-red-500"
              }
            `}
            >
              {feedback === "correct"
                ? "✓"
                : feedback === "partially"
                ? "!"
                : "✗"}
            </div>
            <p
              className={`font-medium
              ${
                feedback === "correct"
                  ? "text-green-700"
                  : feedback === "partially"
                  ? "text-yellow-700"
                  : "text-red-700"
              }
            `}
            >
              {feedback === "correct"
                ? "Correct!"
                : feedback === "partially"
                ? "Partially Correct"
                : "Incorrect"}
            </p>
          </div>
          <p className="text-gray-700 mt-2">
            <span className="font-medium">Your answer:</span> {userAnswer}
          </p>
          <p className="text-gray-700 mt-2">
            <span className="font-medium">Correct answer:</span> {answer}
          </p>
        </div>
      )}

      {isPracticeMode && !reviewed ? (
        <div className="flex justify-between">
          <button
            onClick={handleReset}
            className="py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            disabled={!userAnswer.trim()}
          >
            Clear
          </button>

          <button
            onClick={handleCheck}
            className="py-2 px-4 bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-medium rounded-lg transition-colors"
            disabled={!userAnswer.trim()}
          >
            Check Answer
          </button>
        </div>
      ) : isPracticeMode && reviewed ? (
        <button
          onClick={handleReset}
          className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
        >
          Try again
        </button>
      ) : (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-gray-700">
            <span className="font-medium">Answer:</span> {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const QuizRenderer: React.FC<ToolRendererProps> = ({ results, isLoading }) => {
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const navigate = useNavigate();
  const firebaseAuth = useFirebaseAuth();
  const { refetch } = useContext(YippityUserContext);
  const [isSaving, setIsSaving] = useState(false);

  // Setup API for saving quiz
  const { res, callAPI } = useAuthenticatedFetch({
    url: config.API_URI + `/quiz`,
    method: "POST",
  });

  const togglePracticeMode = () => {
    setIsPracticeMode(!isPracticeMode);
    logEvent(
      "QuizInteraction",
      !isPracticeMode ? "EnablePracticeMode" : "DisablePracticeMode",
      "quiz"
    );
  };

  const handleSaveQuiz = async () => {
    if (!firebaseAuth) {
      toast.error("Please sign in to save quizzes");
      return;
    }

    setIsSaving(true);
    logEvent("ToolAction", "SaveQuiz", "quiz");

    const questionsAndAnswers = results.map((item) => ({
      question: item.question,
      answer: item.answer,
    }));

    callAPI(JSON.stringify({ questionsAndAnswers }));
  };

  // Export to Word-compatible document
  const exportToWordDoc = () => {
    // Create content with proper formatting
    let content = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
<head>
<meta charset="utf-8">
<title>Quiz Questions and Answers</title>
<style>
  body { font-family: 'Calibri', sans-serif; }
  .question { font-weight: bold; margin-top: 20px; }
  .answer { margin-bottom: 20px; }
</style>
</head>
<body>
<h1>Quiz Questions and Answers</h1>`;

    // Add each question and answer
    results.forEach((result: QuizResult, index) => {
      content += `
<div class="question">Question ${index + 1}: ${result.question}</div>
<div class="answer">Answer: ${result.answer}</div>`;
    });

    content += `
</body>
</html>`;

    // Create file and trigger download
    const blob = new Blob([content], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quiz_questions.doc";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Word document downloaded!");
    logEvent("Export", "ExportToWord", "quiz");
    setExportMenuOpen(false);
  };

  // Export to PDF format
  const exportToPDF = () => {
    // Create a simple HTML structure that will print nicely
    let content = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Quiz Questions</title>
  <style>
    body { font-family: Arial, sans-serif; }
    .question { font-weight: bold; margin-top: 15px; }
    .answer { margin-bottom: 15px; }
    @media print {
      @page { margin: 1cm; }
    }
  </style>
</head>
<body>
  <h1>Quiz Questions and Answers</h1>`;

    // Add each question and answer
    results.forEach((result: QuizResult, index) => {
      content += `
  <div class="question">Question ${index + 1}: ${result.question}</div>
  <div class="answer">Answer: ${result.answer}</div>`;
    });

    content += `
</body>
</html>`;

    // Open a new window with print dialog
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();

      // Give the browser a moment to process the document
      setTimeout(() => {
        printWindow.print();
        // Close the window after print dialog is closed
        printWindow.onafterprint = function () {
          printWindow.close();
        };
      }, 500);

      toast.success("PDF prepared for printing!");
      logEvent("Export", "ExportToPDF", "quiz");
    } else {
      toast.error("Please allow popups to enable PDF export");
    }

    setExportMenuOpen(false);
  };

  // Export as text file
  const exportToText = () => {
    let text = "QUIZ QUESTIONS AND ANSWERS\n\n";

    results.forEach((result: QuizResult, index) => {
      text += `Question ${index + 1}: ${result.question}\n`;
      text += `Answer: ${result.answer}\n\n`;
    });

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quiz_questions.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Text file downloaded!");
    logEvent("Export", "ExportToText", "quiz");
    setExportMenuOpen(false);
  };

  // Copy all questions and answers to clipboard
  const copyToClipboard = async () => {
    try {
      let text = "Quiz Questions and Answers:\n\n";

      results.forEach((result: QuizResult, index) => {
        text += `Question ${index + 1}: ${result.question}\n`;
        text += `Answer: ${result.answer}\n\n`;
      });

      await navigator.clipboard.writeText(text);
      toast.success("All questions copied to clipboard!");
      logEvent("Export", "CopyAllQuestions", "quiz");
      setExportMenuOpen(false);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
      console.error("Failed to copy: ", err);
    }
  };

  // Handle the response from saving
  useEffect(() => {
    if (res.statusCode === 200) {
      setIsSaving(false);
      refetch();
      toast.success("Quiz saved successfully!");
      navigate(`/quiz/${res.data.quizId}`);
    } else if (res.statusCode && res.statusCode !== 200) {
      setIsSaving(false);
      toast.error("Failed to save quiz");
    }
  }, [res]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-700">
          {results.length} Open-Ended Questions
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={togglePracticeMode}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${
                isPracticeMode
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
            `}
          >
            {isPracticeMode ? "View Mode" : "Practice Mode"}
          </button>

          <button
            onClick={handleSaveQuiz}
            disabled={isSaving || results.length === 0}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center
              ${
                isSaving
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-green-100 text-green-800 hover:bg-green-200"
              }
            `}
          >
            {isSaving ? (
              <>
                <HashLoader size={14} color="#888" className="mr-2" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <AiFillSave className="mr-2" />
                <span>Save Quiz</span>
              </>
            )}
          </button>

          {/* Export Menu */}
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-800 font-medium rounded-lg flex items-center transition-colors"
            >
              <BiExport className="mr-2" />
              Export
            </button>

            {exportMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 overflow-hidden border border-gray-100">
                <div className="py-1">
                  <button
                    onClick={copyToClipboard}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaCopy className="mr-2" />
                    Copy all to clipboard
                  </button>
                  <button
                    onClick={exportToWordDoc}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaFileWord className="mr-2" />
                    Export as Word Doc
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaDownload className="mr-2" />
                    Export as PDF
                  </button>
                  <button
                    onClick={exportToText}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaFileExport className="mr-2" />
                    Export as Text
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {results.map((result: QuizResult, index) => (
          <QuizQuestion
            key={index}
            question={result.question}
            answer={result.answer}
            index={index}
            isPracticeMode={isPracticeMode}
          />
        ))}
      </div>

      {isPracticeMode && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">Practice Mode Tips</h4>
          <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
            <li>Type your answer in the text field and click "Check Answer"</li>
            <li>Your answer will be evaluated against the correct answer</li>
            <li>
              Evaluation is simple - exact matches are correct, partial matches
              may be partially correct
            </li>
            <li>Click "Try again" to reset and try the question again</li>
          </ul>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-2">Export Options</h4>
        <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
          <li>
            <strong>Word Document:</strong> Export in Microsoft Word format for
            editing and formatting
          </li>
          <li>
            <strong>PDF Export:</strong> Create a printable PDF document via
            your browser's print function
          </li>
          <li>
            <strong>Text Export:</strong> Plain text format for all questions
            and answers
          </li>
          <li>
            <strong>Save Quiz:</strong> Store your quiz in your Yippity account
            for future access
          </li>
        </ul>
      </div>
    </div>
  );
};

export default QuizRenderer;
